import React from 'react';

import { isDefined } from '../../../utils/variables';
import { MouseInputData } from '../../../viewer/utilities/input-event/input-event-data-extractor';
import { Point2d } from '../../../viewer/utilities/math-helpers';
import { MenuItem } from '../context-menu/components/menu-item';
import { SubMenu } from '../context-menu/components/sub-menu';
import { ContextMenu } from '../context-menu/context-menu';

export interface ContextMenuItem {
  action: (e?: MouseInputData) => Promise<void> | void;
  children?: ContextMenuItem[];
  disabled?: boolean;
  hotkey?: string;
  title: string;
  toggled?: boolean;
}

export interface SkyViewContextMenuProps {
  items: ContextMenuItem[];
  pos: Point2d;
}

const SkyViewContextMenu = (props: SkyViewContextMenuProps) => {
  // Holds current status of checkbox for checkable items.
  const [toggledItems, setToggledItems] = React.useState<Map<ContextMenuItem, boolean>>(new Map());

  /**
   * When items changes, save each toggable item together with the toggled value.
   * These values are used as default value for checkboxes.
   */
  React.useEffect(() => {
    const map = new Map<ContextMenuItem, boolean>();

    const iterate = (items: ContextMenuItem[]) => {
      for (const item of items) {
        if (isDefined(item.toggled)) {
          map.set(item, item.toggled);
        }
        if (item.children) {
          iterate(item.children);
        }
      }
    };

    iterate(props.items);
    setToggledItems(map);
  }, [props.items]);

  /**
   * Toggles the specified item (checks/unchecks checkbox).
   * Rerenders component and context menus gets it checkbox updated.
   */
  const toggleItem = (item: ContextMenuItem) => {
    setToggledItems(new Map(toggledItems.set(item, !toggledItems.get(item))));
  };

  /**
   * Returns a context menu component, given a list of context menu items.
   * @param items Context menu items.
   * @param isRootMenu Whether the current menu is the root menu.
   */
  const buildFromItems = (items: ContextMenuItem[], isRootMenu = true) => {
    const children = items.map((item) =>
      item.children ? (
        <SubMenu key={item.title} label={item.title}>
          {buildFromItems(item.children, false)}
        </SubMenu>
      ) : (
        <MenuItem
          disabled={() => item.disabled}
          hotkey={item.hotkey}
          key={item.title}
          label={item.title}
          toggled={() => toggledItems.get(item)}
          onClick={async () => {
            if (isDefined(item.toggled)) {
              toggleItem(item);
            }

            await item.action();
          }}
        />
      ),
    );

    return isRootMenu ? (
      <ContextMenu left={props.pos.x} top={props.pos.y}>
        {children}
      </ContextMenu>
    ) : (
      children
    );
  };

  return <>{buildFromItems(props.items)}</>;
};

export { SkyViewContextMenu };
